/**
 * Figure 5, step 3: the exchange.
 *
 * Two lists, and the split between them is the design. **"Michael needs from you" is a named checklist,
 * not a generic upload box** - the design document is blunt about why: "Reps do not want files, they
 * want three specific things." An upload answers a named item, which is what makes the rep's side
 * resolve automatically and what stops a prospect sending the wrong document and believing they are
 * done. **"Shared with you" is the other direction**, and it exists because of the promise underneath
 * it: "Everything shared stays here permanently" is the answer to the lost-correspondence complaint,
 * and under D-06 it is a committed guarantee rather than a wireframe aspiration.
 *
 * Withdrawn items are filtered here rather than server-side. `GET /guest/v1/requests` returns them so
 * the API can express what the rep actually did; a prospect has no use for a request that no longer
 * stands, so the surface hides it.
 *
 * ## The upload
 *
 * One reusable `<input type="file">` for every checklist item and anything else, and no drag-and-drop
 * target. D-18 defers purpose-built capture and the reasoning holds: the native picker already offers
 * the camera on both mobile platforms, and a drop zone is a desktop idiom on a surface built for a
 * phone. The `capture` attribute is deliberately absent - a prospect may well be sending a PDF their
 * accountant emailed them, and forcing the camera would make that impossible.
 */
import { useRef, useState } from 'react';
import { Button, Card, FormattedRequest, Notice, Page, Progress, Row, StatusPill } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';
import { formatBytes } from '../upload/tus';
import type { GuestChecklistItem, GuestDocument, GuestExchange } from '@iqz-ddds/contracts';

export interface UploadState {
  readonly requestId: string | null;
  readonly filename: string;
  readonly sentBytes: number;
  readonly totalBytes: number;
  /**
   * Which half of the wait this is.
   *
   * `sending` has a progress bar because there is progress to show. `checking` does not: section 09's
   * scan reports nothing until it has a verdict, and a bar that sat at 100% would be claiming to know
   * something. The two are named separately because a prospect who has watched a bar fill and then
   * sees it keep sitting there concludes it has stalled.
   */
  readonly stage: 'sending' | 'checking';
}

/** What to say about an upload that has finished, where there is anything to say. */
export interface UploadOutcome {
  readonly tone: 'warn' | 'error';
  readonly text: string;
}

export interface ExchangeScreenProps {
  readonly exchange: GuestExchange;
  readonly requests: readonly GuestChecklistItem[];
  readonly documents: readonly GuestDocument[];
  /** Null when nothing is in flight. One at a time, deliberately; see the note below. */
  readonly uploading: UploadState | null;
  /** Null when the last upload committed, which needs no words: the document is in the list. */
  readonly outcome?: UploadOutcome | null;
  readonly onUpload: (input: { files: readonly File[]; requestId: string | null }) => Promise<void>;
  readonly error?: unknown;
  readonly isGenericLink?: boolean;
}

/** A date a person reads, in their own locale. Never a raw ISO string. */
function shortDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ExchangeScreen({
  exchange,
  requests,
  documents,
  uploading,
  outcome,
  onUpload,
  error,
  isGenericLink = false,
}: ExchangeScreenProps): React.JSX.Element {
  // One hidden input, reused. A file input per checklist item would be a dozen elements a screen
  // reader walks through, and only one can be in use at a time anyway.
  const picker = useRef<HTMLInputElement>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | null>(null);
  const [staged, setStaged] = useState<ReadonlyMap<string | null, readonly File[]>>(() => new Map());

  const outstanding = requests.filter((item) => item.state !== 'withdrawn');
  const fromRep = documents.filter((document) => document.origin === 'rep');
  const fromMe = documents.filter((document) => document.origin === 'contact');

  const choose = (requestId: string | null): void => {
    setPendingRequestId(requestId);
    picker.current?.click();
  };

  const stage = (incoming: FileList | null): void => {
    if (incoming === null) return;
    const selected = [...incoming];
    if (selected.length === 0) return;
    setStaged((current) => {
      const next = new Map(current);
      const existing = next.get(pendingRequestId) ?? [];
      const known = new Set(existing.map((file) => `${file.name}:${String(file.size)}`));
      next.set(pendingRequestId, [...existing, ...selected.filter((file) => !known.has(`${file.name}:${String(file.size)}`))]);
      return next;
    });
  };

  const submit = (requestId: string | null): void => {
    const files = staged.get(requestId) ?? [];
    if (files.length === 0) return;
    setStaged((current) => {
      const next = new Map(current);
      next.delete(requestId);
      return next;
    });
    void onUpload({ files, requestId });
  };

  const filesFor = (requestId: string | null): readonly File[] => staged.get(requestId) ?? [];

  const remove = (requestId: string | null, index: number): void => {
    setStaged((current) => {
      const files = current.get(requestId) ?? [];
      const next = new Map(current);
      const remaining = files.filter((_, at) => at !== index);
      if (remaining.length === 0) next.delete(requestId);
      else next.set(requestId, remaining);
      return next;
    });
  };

  return (
    <Page
      eyebrow="Secure documents"
      title={`Your documents with ${exchange.repName}`}
      lede="Everything shared here stays available to you."
    >
      <FailureNotice error={error} />

      {outcome !== null && outcome !== undefined && <Notice tone={outcome.tone}>{outcome.text}</Notice>}

      <input
        ref={picker}
        type="file"
        multiple
        // Off-screen rather than `display: none`, which some browsers refuse to click programmatically.
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
        onChange={(event) => {
          stage(event.target.files);
          // Reset first, so choosing the same files again still fires a change event.
          event.target.value = '';
        }}
        aria-hidden="true"
        tabIndex={-1}
      />

      {uploading !== null && uploading.stage === 'sending' && (
        <Card title="Sending">
          <p className="ddds-row__name">{uploading.filename}</p>
          <p className="ddds-row__meta">
            {formatBytes(uploading.sentBytes)} of {formatBytes(uploading.totalBytes)}
          </p>
          <Progress
            value={uploading.totalBytes === 0 ? 0 : uploading.sentBytes / uploading.totalBytes}
            label={`Sending ${uploading.filename}`}
          />
          <p className="ddds-row__meta">
            You can leave this page open. If your connection drops, it continues from where it stopped.
          </p>
        </Card>
      )}

      {/* The bytes have all arrived and the scan has not finished. R5 puts that check between the
          upload and the document, so there is a gap where the file is neither still sending nor yet
          in the list below - and it is the gap that made an upload look like it had not happened.
          No progress bar: the scanner reports nothing until it has a verdict, so a bar here would be
          an animation pretending to be information. An <output> announces the change to a screen
          reader, which a person who cannot see the card appearing otherwise never learns about. */}
      {uploading !== null && uploading.stage === 'checking' && (
        <Card title="Checking">
          <p className="ddds-row__name">{uploading.filename}</p>
          <output className="ddds-row__meta">
            Sent. We are checking this file before adding it - this usually takes a few seconds.
          </output>
        </Card>
      )}

      {outstanding.length > 0 && (
        <Card title={`${exchange.repName} needs from you`}>
          <ul className="ddds-list">
                {outstanding.map((item) => {
                  const files = filesFor(item.id);
                  let meta = `Asked ${shortDate(item.requestedAt)}`;
                  if (files.length > 0) {
                    const noun = files.length === 1 ? 'document' : 'documents';
                    meta = `${String(files.length)} ${noun} ready`;
                  }
                  return <Row
                    key={item.id}
                    name={
                      <>
                        <FormattedRequest html={item.label} />
                        {item.state !== 'fulfilled' && (
                          <div className="ddds-request-actions">
                            <div className="ddds-row__actions">
                              <Button size="small" onClick={() => choose(item.id)} disabled={uploading !== null} label="Add documents for this request">
                                Add
                              </Button>
                              <Button size="small" onClick={() => submit(item.id)} disabled={uploading !== null || files.length === 0} label="Upload documents for this request">
                                Upload
                              </Button>
                            </div>
                            {files.length > 0 && (
                              <ul className="ddds-selected-files" aria-label="Selected documents">
                                {files.map((file, index) => (
                                  <li key={`${file.name}:${String(file.size)}:${String(index)}`}>
                                    <span>{file.name}</span>
                                    <Button variant="quiet" size="small" label={`Remove ${file.name}`} onClick={() => remove(item.id, index)}>
                                      Remove
                                    </Button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </>
                    }
                    meta={meta}
                    action={item.state === 'fulfilled' ? <StatusPill tone="done">Sent</StatusPill> : undefined}
                  />
                })}
          </ul>
        </Card>
      )}

      <Card title="Shared with you">
        {fromRep.length === 0 ? (
          <p className="ddds-row__meta">Nothing yet. Anything {exchange.repName} sends will appear here.</p>
        ) : (
          <ul className="ddds-list">
            {fromRep.map((document) => (
              <Row
                key={document.id}
                name={
                  // A plain link, not a fetch. The browser handles the 302 to a signed URL and the
                  // `Content-Disposition` filename, which is what makes a download behave like one on
                  // a phone. `download` is deliberately absent so iOS opens a PDF in its viewer.
                  <a href={`/guest/v1/documents/${document.id}/content`}>{document.name}</a>
                }
                meta={shortDate(document.addedAt)}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* No status pill on these rows. Every document that reaches this list has already cleared
          section 09's scan and committed, so a pill here would be the same word on every row saying
          what the heading above it already said. A document still in flight is the "Sending" or
          "Checking" card at the top of the page, not a row down here with a different pill. */}
      {fromMe.length > 0 && (
        <Card title="You sent">
          <ul className="ddds-list">
            {fromMe.map((document) => (
              <Row key={document.id} name={document.name} meta={shortDate(document.addedAt)} />
            ))}
          </ul>
        </Card>
      )}

      <div className="ddds-row__actions">
        <Button variant="quiet" onClick={() => choose(null)} disabled={uploading !== null}>
          {isGenericLink ? 'Add documents' : 'Add something else'}
        </Button>
        <Button onClick={() => submit(null)} disabled={uploading !== null || filesFor(null).length === 0}>
          Upload
        </Button>
      </div>
    </Page>
  );
}
